<template>
    <div v-bind="$attrs" @click="tryOpenModal" class="cursor-pointer">
        <slot></slot>
    </div>
    <ModalCustom
        :is-open="state.isModalOpen"
        @close="tryCloseLoadModal"
        title="Select Tags"
        z-index="1001"
    >
        <div class="w-[80vw] max-h-[50vh] overflow-y-auto">
            <div class="flex gap-2">
                <div
                    v-for="selectedPlay in state.selectedPlays"
                    :key="selectedPlay.play_id"
                >
                    <div
                        @click="selectedPlay.is_my_play && select(selectedPlay)"
                        v-show="selectedPlay.is_visible"
                        class="
                            flex
                            items-center
                            rounded-md
                            px-2
                            py-1
                            text-sm
                            font-medium
                            ring-1 ring-inset
                            gap-1
                            cursor-pointer
                        "
                        :class="{
                            'ring-blue-700/10 text-blue-700 bg-blue-50':
                                selectedPlay.is_my_play,
                            'ring-green-700/10 text-green-700 bg-green-50':
                                !selectedPlay.is_my_play,
                        }"
                    >
                        <span v-if="selectedPlay.is_my_play"
                            >{{ selectedPlay.play_name }} ({{
                                selectedPlay.playbook_title
                            }})</span
                        >
                        <span v-else
                            >{{ selectedPlay.play_name }} ({{
                                selectedPlay.user_name
                            }})</span
                        >
                        <ion-icon
                            name="close-outline"
                            v-show="selectedPlay.is_my_play"
                        ></ion-icon>
                    </div>
                </div>
            </div>
            <loading-circle class="m-12" v-if="state.isLoading" />
            <div v-else class="mt-2 p-4 flex gap-4 items-start">
                <div
                    class="
                        grid grid-cols-3
                        gap-4
                        lg:grid-cols-4
                        flex-grow flex-shrink
                    "
                >
                    <div
                        v-if="playInActivePlaybook.length === 0"
                        class="col-span-4 self-center"
                    >
                        <div class="text-center text-gray-500">
                            No plays in this playbook
                        </div>
                    </div>
                    <div
                        v-for="play in playInActivePlaybook"
                        :key="play.play_id"
                        class="
                            p-8
                            bg-white
                            shadow-xl
                            rounded-lg
                            border-[3px] border-gray-200
                            text-center
                            cursor-pointer
                            hover:border-green-500
                        "
                        :class="{
                            'border-green-500': state.selectedPlays.find(
                                (p) => p.play_id === play.play_id
                            ),
                        }"
                        @click="select(play)"
                    >
                        <div class="block text-xl">
                            {{ play.play_name }}
                        </div>
                        <div class="mt-2">
                            <img
                                :src="
                                    play.thumbnail_url ??
                                    `${previewUrl}/${play.play_preview}`
                                "
                                alt="Play Thumbnail"
                                class="
                                    bg-white
                                    flex-grow flex-shrink
                                    w-full
                                    rounded-md
                                    my-4
                                    object-contain
                                "
                                :style="{
                                    backgroundImage: `linear-gradient(to bottom, #fffa, #fffa), url('${play.background}')`,
                                    backgroundSize: 'contain',
                                    backgroundRepeat: 'no-repeat',
                                    aspectRatio: 2.07339829981,
                                }"
                                onerror="this.onerror=null;this.src='/default-images/dummy-play.png';this.style.backgroundImage = 'none';"
                            />
                        </div>
                    </div>
                </div>
                <div
                    class="
                        flex-grow-0 flex-shrink-0
                        w-[25%]
                        bg-white
                        rounded-md
                        shadow-md
                        p-4
                    "
                >
                    <h4 class="text-xl font-medium mb-2">Playbooks</h4>
                    <ul>
                        <li
                            class="text-gray-500 p-2 mb-2"
                            v-if="state.playbooks.length === 0"
                        >
                            No Playbooks Found
                        </li>
                        <template v-else>
                            <li
                                class="mb-2 ml-0 p-0"
                                v-for="playbook in state.playbooks"
                                :key="playbook.playbook_id"
                            >
                                <button
                                    type="button"
                                    @click="state.activePlaybook = playbook"
                                    class="
                                        block
                                        py-2
                                        px-3
                                        bg-gray-200
                                        rounded-md
                                        hover:text-white hover:bg-primary
                                        w-full
                                    "
                                    :class="{
                                        'bg-primary text-white':
                                            state.activePlaybook
                                                ?.playbook_id ===
                                            playbook.playbook_id,
                                    }"
                                >
                                    {{ playbook.playbook_title }}
                                </button>
                            </li>
                        </template>
                    </ul>
                </div>
            </div>
        </div>
    </ModalCustom>
</template>

<script setup>
import { computed, onMounted, reactive } from "vue";
import LoadingCircle from "./LoadingCircle.vue";
import ModalCustom from "./ModalCustom.vue";
import AppButton from "./AppButton.vue";

const props = defineProps([
    "previewUrl",
    "playbookApi",
    "playApi",
    "whiteboardApi",
    "updateVideoTagsApi",
    "showNewPlayButton",
    "video",
]);
const emit = defineEmits(["loaded"]);
const state = reactive({
    isModalOpen: false,
    activePlaybook: null,
    playbooks: [],
    tags: [],
    selectedPlays: [],
});

const playInActivePlaybook = computed(() => {
    return state.activePlaybook?.plays ?? [];
});

const tryCloseLoadModal = () => {
    if (state.isLoading) {
        return;
    }

    state.selectedPlays.value = [];
    state.tags = [];
    state.isModalOpen = false;
};

async function tryOpenModal() {
    emit("loaded");
    state.isModalOpen = true;
    state.isLoading = true;
    try {
        await refreshData();
    } catch {}
    state.isLoading = false;
}

function select(selectedPlay) {
    const selected = state.selectedPlays.find(
        (play) => play.play_id === selectedPlay.play_id
    );

    if (selected) {
        state.selectedPlays = state.selectedPlays.filter(
            (play) => play.play_id !== selectedPlay.play_id
        );
    } else {
        state.selectedPlays = [
            ...state.selectedPlays,
            {
                ...selectedPlay,
                is_my_play: true,
                is_visible: true,
                playbook_title: state.activePlaybook.playbook_title,
            },
        ];
    }

    updatePlayTags();
}

async function updatePlayTags() {
    await axios.post(props.updateVideoTagsApi, {
        video_id: props.video.id,
        tags: state.selectedPlays.map((play) => play.play_id).join(","),
    });
}

async function refreshData() {
    const teamId = new URLSearchParams(window.location.search).get("teamId");
    try {
        state.playbooks = (
            await axios.get(props.playbookApi, {
                params: {
                    teamId,
                },
            })
        ).data;
        state.activePlaybook = state.playbooks[0];
    } catch {}

    state.tags = (await axios.get(`/video/${props.video.id}/tags`, {})).data;
    state.selectedPlays = state.tags;
}

onMounted(() => {
    refreshData();
});
</script>

<style scoped></style>
